import { createClient } from '@supabase/supabase-js';
import * as mock from './mockData';

const url = process.env.REACT_APP_SUPABASE_URL;
const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
export const supabaseConfigured = Boolean(url && anonKey);

// Loud + obvious boot-time failure if env is missing. Previously the app
// would just silently route every auth call into a null client (web) or
// cause `supabase.auth.signInWithPassword` to issue fetch against the
// WebView's own origin on Capacitor Android, which surfaces as the
// confusing "Failed to fetch" toast on the login screen. The dev console
// now screams the real reason: your `.env` is empty.
if (!supabaseConfigured && typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  console.error(
    '[rivl] Supabase is not configured. Copy frontend/.env.example to ' +
    'frontend/.env (or set REACT_APP_SUPABASE_URL + ' +
    'REACT_APP_SUPABASE_ANON_KEY in your build env) and rebuild. ' +
    'Current values: url=' + String(url) + ' anonKey=' +
    (anonKey ? '(set, length=' + anonKey.length + ')' : '(missing)')
  );
}

// Supabase JS v2 uses navigator.locks to serialize auth operations across
// browser tabs. In a mobile-first PWA / Capacitor shell we never have two
// concurrent tabs — but React StrictMode double-invokes effects in dev,
// and webpack HMR instantiates a second module graph on each hot swap, so
// two auth requests race for the lock. The newer one steals with
// `{ steal: true }` which throws AbortError on the older — logged as a
// scary red "Lock stolen" error even though auth continues to work.
//
// Replace the locking primitive with a no-op pass-through. See
// https://github.com/supabase/supabase-js/issues/963 — the officially
// supported way to opt out.
const noopLock = async (_name, _acquireTimeout, fn) => await fn();

export const supabase = supabaseConfigured
  ? createClient(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        lock: noopLock,
      },
    })
  : null;

// =====================================================================
// MAPPERS — translate DB row → UI shape (matches the original mockData)
// =====================================================================
const mapEvent = (e) => ({
  id: e.id,
  name: e.name,
  organizer: e.organizer_name,
  organizer_id: e.organizer_id,
  type: e.type,
  date: e.date,
  distanceKm: Number(e.distance_km),
  location: e.location,
  participants: e.participants ?? 0,
  capacity: e.capacity,
  status: e.status,
  raceState: e.race_state,
  image: e.image,
  bibMode: e.bib_mode,
  medalEnabled: e.medal_enabled,
  description: e.description,
  fee: Number(e.fee || 0),
  currency: e.currency || 'NGN',
  isPaid: !!e.is_paid,
  startLat: e.start_lat != null ? Number(e.start_lat) : null,
  startLng: e.start_lng != null ? Number(e.start_lng) : null,
  finishLat: e.finish_lat != null ? Number(e.finish_lat) : null,
  finishLng: e.finish_lng != null ? Number(e.finish_lng) : null,
  eventMode: e.event_mode || 'physical',
  checkpoints: Array.isArray(e.checkpoints) ? e.checkpoints : [],
  joinDeadline: e.join_deadline || null,
  bankName: e.bank_name || null,
  accountNumber: e.account_number || null,
  accountName: e.account_name || null,
  raceStartedAt: e.race_started_at || null,
  activationCode: e.activation_code || null,
});

const mapParticipation = (p) => ({
  id: p.id,
  eventId: p.event_id,
  userId: p.user_id,
  bibNumber: p.bib_number,
  state: p.state,
  checkedInAt: p.checked_in_at,
  finishedAt: p.finished_at,
  finishTimeSec: p.finish_time_sec,
  qrToken: p.qr_token,
  coverage: p.coverage_pct,
  anomalies: p.anomalies,
  gpsTrack: Array.isArray(p.gps_track) ? p.gps_track : null,
});

const mapRun = (r) => {
  // The `date` column was DATE before round17_delta — that would truncate
  // our client-side ISO timestamp to a calendar day. When we detect that
  // shape here we fall back to `created_at` (timestamptz, which
  // savePersonalRun also stamps with the true run start), so run history
  // shows the actual wall-clock time the runner began — not midnight local.
  const looksLikeBareDate = typeof r.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(r.date);
  const effectiveDate = looksLikeBareDate ? (r.created_at || r.date) : r.date;
  return {
    id: r.id,
    date: effectiveDate,
    distanceKm: Number(r.distance_km),
    durationSec: r.duration_sec,
    paceSecPerKm: r.pace_sec_per_km,
    paceMinPerKm: r.pace_sec_per_km
      ? `${Math.floor(r.pace_sec_per_km / 60)}:${String(r.pace_sec_per_km % 60).padStart(2, '0')}`
      : '—',
    visibility: r.visibility,
    gpsTrack: Array.isArray(r.gps_track) ? r.gps_track : null,
  };
};

const mapFeed = (f) => ({
  id: f.id,
  type: f.type,
  user: f.profiles?.name || 'Unknown',
  text: f.text,
  time: relTime(f.created_at),
  verified: f.verified,
});

const mapLeaderboardRow = (row, currentUserId) => ({
  rank: row.rank,
  name: row.name,
  bib: row.bib_number,
  time: row.finish_time,
  verified: row.verified,
  pace: paceFromTime(row.finish_time_sec, row.distance_km),
  isYou: row.user_id === currentUserId,
});

const mapFriend = (f) => ({
  id: f.profile?.id || f.friend_id,
  name: f.profile?.name || 'Unknown',
  handle: f.profile?.handle ? '@' + f.profile.handle : '—',
  avatar: f.profile?.avatar_url || null,
  status: f.status,
  races: 0,
});

function relTime(iso) {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

function paceFromTime(totalSec, distanceKm) {
  if (!totalSec || !distanceKm) return '—';
  const sec = Math.floor(totalSec / distanceKm);
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

const throwIf = (e) => {
  if (!e) return;
  // Supabase-js sometimes throws a TypeError when it has already consumed the
  // response body once (typically on RLS denials with no body). Surface a
  // friendlier message in that case so toasts are usable.
  const msg = e.message || '';
  if (msg.includes('body stream already read')) {
    const friendly = new Error(
      'Permission denied. Make sure you are signed in and have the right role for this action.'
    );
    friendly.cause = e;
    throw friendly;
  }
  // PostgREST RLS denial (42501) — translate to plain English.
  if (e.code === '42501') {
    throw new Error('Permission denied by database policy.');
  }
  throw e;
};
async function uid() {
  if (!supabase) return null;
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id ?? null;
}

// =====================================================================
// API — single integration layer for the whole mobile app
// Falls back to mock data when Supabase isn't configured (dev mode).
// =====================================================================
export const api = {
  // ---------- Profile ----------
  async getMyProfile() {
    if (!supabase) return mock.mockUser;
    const id = await uid();
    if (!id) return null;
    const { data, error } = await supabase.from('profiles').select('*').eq('id', id).maybeSingle();
    throwIf(error);
    return data;
  },

  // Update bio fields on my profile.
  async updateMyProfile(patch) {
    if (!supabase) return patch;
    const id = await uid();
    if (!id) throw new Error('Not signed in');
    const payload = {};
    if (patch.name !== undefined) payload.name = patch.name;
    if (patch.handle !== undefined) payload.handle = patch.handle;
    if (patch.firstName !== undefined) payload.first_name = patch.firstName || null;
    if (patch.lastName !== undefined) payload.last_name = patch.lastName || null;
    if (patch.birthday !== undefined) payload.birthday = patch.birthday || null;
    if (patch.gender !== undefined) payload.gender = patch.gender || null;
    if (patch.weightKg !== undefined) payload.weight_kg = patch.weightKg ? Number(patch.weightKg) : null;
    if (patch.heightCm !== undefined) payload.height_cm = patch.heightCm ? Number(patch.heightCm) : null;
    if (patch.country !== undefined) payload.country = patch.country || null;
    // If firstName/lastName were edited and `name` wasn't explicitly set,
    // keep the legacy `name` column in sync so existing UI keeps working.
    if ((patch.firstName !== undefined || patch.lastName !== undefined) && patch.name === undefined) {
      const composed = [patch.firstName, patch.lastName].filter(Boolean).join(' ').trim();
      if (composed) payload.name = composed;
    }
    const { data, error } = await supabase.from('profiles').update(payload).eq('id', id).select().maybeSingle();
    throwIf(error);
    return data;
  },

  // Aggregate workout stats for Home / Profile cards.
  // Returns { totalRuns, totalKm, avgKm, bestPaceSec, lastRunAt }
  async getMyStats() {
    if (!supabase) return { totalRuns: 12, totalKm: 73.8, avgKm: 6.15, bestPaceSec: 280, lastRunAt: null };
    const id = await uid();
    if (!id) return { totalRuns: 0, totalKm: 0, avgKm: 0, bestPaceSec: null, lastRunAt: null };
    const { data, error } = await supabase
      .from('personal_runs')
      .select('distance_km, pace_sec_per_km, created_at')
      .eq('user_id', id);
    throwIf(error);
    const runs = data || [];
    const totalRuns = runs.length;
    const totalKm = runs.reduce((s, r) => s + Number(r.distance_km || 0), 0);
    const avgKm = totalRuns > 0 ? totalKm / totalRuns : 0;
    const paces = runs.map((r) => r.pace_sec_per_km).filter(Boolean);
    const bestPaceSec = paces.length ? Math.min(...paces) : null;
    const lastRunAt = runs.length
      ? runs.map((r) => r.created_at).sort().slice(-1)[0]
      : null;
    return { totalRuns, totalKm, avgKm, bestPaceSec, lastRunAt };
  },

  // Events the current user has joined.
  // Pass { past: true } to get *finished* events for the "Past events" tab
  // with badge data (joined = participation, verified = completion).
  async getMyEvents({ past = false, limit = 50 } = {}) {
    if (!supabase) return mock.mockEvents.slice(0, 3);
    const id = await uid();
    if (!id) return [];
    const { data, error } = await supabase
      .from('participations')
      .select('state, finish_time_sec, created_at, events(*)')
      .eq('user_id', id)
      .order('created_at', { ascending: false })
      .limit(limit);
    throwIf(error);
    const nowMs = Date.now();
    const rows = (data || []).filter((r) => r.events).map((r) => ({
      ...mapEvent(r.events),
      myState: r.state,
      myFinishSec: r.finish_time_sec,
      myParticipation: true, // joined = always true here
      myCompleted: r.state === 'verified' || r.state === 'finished',
    }));
    if (past) {
      return rows.filter((e) => new Date(e.date).getTime() < nowMs || e.myCompleted);
    }
    // upcoming = not yet started, not finished
    return rows.filter((e) => new Date(e.date).getTime() >= nowMs && !e.myCompleted);
  },

  // ---------- Events ----------
  // Returns *open, upcoming* events by default. Pass { includePast: true } to
  // include past events as well. Pass { excludeJoined: true } to filter out
  // events the current user has already joined (used by Home's Discover).
  async getEvents({ includePast = false, excludeJoined = false } = {}) {
    if (!supabase) return mock.mockEvents;
    let q = supabase.from('events').select('*').eq('status', 'open').order('date');
    // We keep events whose start date is in the future OR whose race is
    // currently live — so spectators can still discover a race in progress.
    if (!includePast) {
      const now = new Date().toISOString();
      q = q.or(`date.gte.${now},race_state.eq.live`);
    }
    const { data, error } = await q.limit(200);
    throwIf(error);
    let rows = (data || []).map(mapEvent);
    if (excludeJoined) {
      const me = await uid();
      if (me) {
        const { data: joined } = await supabase
          .from('participations')
          .select('event_id')
          .eq('user_id', me)
          .limit(1000);
        const joinedSet = new Set((joined || []).map((r) => r.event_id));
        rows = rows.filter((e) => !joinedSet.has(e.id));
      }
    }
    return rows;
  },

  async getEvent(id) {
    if (!supabase) return mock.mockEvents.find((e) => e.id === id);
    const { data, error } = await supabase.from('events').select('*').eq('id', id).maybeSingle();
    throwIf(error);
    return data ? mapEvent(data) : null;
  },

  // ---------- Active participation (drives the Home active card) ----------
  async getActiveParticipation() {
    if (!supabase) return { participation: mock.mockParticipation, event: mock.mockEvents[0] };
    const id = await uid();
    if (!id) return null;
    const { data, error } = await supabase
      .from('participations')
      .select('*, events(*)')
      .eq('user_id', id)
      .not('state', 'in', '(verified,unverified)')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    throwIf(error);
    if (!data) return null;
    return { participation: mapParticipation(data), event: mapEvent(data.events) };
  },

  // ---------- Activity feed ----------
  async getFeed() {
    if (!supabase) return mock.mockFeed;
    const { data, error } = await supabase
      .from('activity_feed')
      .select('*, profiles(name)')
      .order('created_at', { ascending: false })
      .limit(20);
    throwIf(error);
    return (data || []).map(mapFeed);
  },

  // ---------- Leaderboard ----------
  async getLeaderboard(eventId) {
    if (!supabase) return mock.mockLeaderboard;
    if (!eventId) return [];
    const me = await uid();
    const { data: ev } = await supabase.from('events').select('distance_km').eq('id', eventId).maybeSingle();
    const distance = Number(ev?.distance_km || 0);
    const { data, error } = await supabase.from('v_leaderboard').select('*').eq('event_id', eventId).limit(50);
    throwIf(error);
    return (data || []).map((r) => mapLeaderboardRow({ ...r, distance_km: distance }, me));
  },

  // Live participants (still racing). For the "watch live" view of an
  // in-progress event. We pull straight from `participations` because the
  // v_leaderboard view only exposes verified finishers.
  async getLiveParticipants(eventId) {
    if (!supabase || !eventId) return [];
    const { data, error } = await supabase
      .from('participations')
      .select('user_id, bib_number, state, started_at, profiles(name, handle)')
      .eq('event_id', eventId)
      .in('state', ['live', 'finished', 'verified'])
      .order('state', { ascending: true });
    throwIf(error);
    return (data || []).map((r) => ({
      userId: r.user_id,
      bib: r.bib_number,
      state: r.state,
      startedAt: r.started_at,
      name: r.profiles?.name || 'Runner',
      handle: r.profiles?.handle || '',
    }));
  },

  // Returns ALL participants of an event (any state), for the organizer's
  // Participants section on EventDetail. Newest joiners first.
  async getEventParticipants(eventId) {
    if (!supabase || !eventId) return [];
    const { data, error } = await supabase
      .from('participations')
      .select('user_id, bib_number, state, created_at, finish_time_sec, profile:profiles(id,name,handle,avatar_url)')
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });
    throwIf(error);
    return (data || []).map((r) => ({
      userId: r.user_id,
      bib: r.bib_number,
      state: r.state,
      joinedAt: r.created_at,
      finishTimeSec: r.finish_time_sec,
      name: r.profile?.name || 'Runner',
      handle: r.profile?.handle ? '@' + r.profile.handle : '',
      avatar: r.profile?.avatar_url || null,
    }));
  },

  // ---------- Friends ----------
  // Recent friends' activity — shows verified finishes by my accepted friends.
  async getFriendsFeed(limit = 8) {
    if (!supabase) {
      return [
        { id: 'f1', name: 'Amara Okafor', text: 'finished Lagos City 10K in 47:21', distanceKm: 10, verified: true, createdAt: new Date().toISOString() },
        { id: 'f2', name: 'Tunde Adeyemi', text: 'completed a 5.2km morning run', distanceKm: 5.2, verified: false, createdAt: new Date(Date.now() - 3600000 * 4).toISOString() },
      ];
    }
    const me = await uid();
    if (!me) return [];
    const { data: fr } = await supabase
      .from('friendships')
      .select('friend_id')
      .eq('user_id', me)
      .eq('status', 'friend');
    const friendIds = (fr || []).map((r) => r.friend_id);
    if (!friendIds.length) return [];
    const { data, error } = await supabase
      .from('activity_feed')
      .select('id, type, text, verified, created_at, user_id, profiles(name)')
      .in('user_id', friendIds)
      .order('created_at', { ascending: false })
      .limit(limit);
    throwIf(error);
    return (data || []).map((r) => ({
      id: r.id,
      name: r.profiles?.name || 'A friend',
      text: r.text,
      type: r.type,
      verified: !!r.verified,
      createdAt: r.created_at,
    }));
  },

  // Returns recent runs by the user + their friends, joined to profile info,
  // for the "Global" tab in /friends. Each row:
  //   { id, userId, userName, avatarUrl, distanceKm, durationSec, verified,
  //     type, createdAt }
  async getFriendsAndOwnRunsFeed({ limit = 30 } = {}) {
    if (!supabase) return [];
    const me = await uid();
    if (!me) return [];
    const { data: fr } = await supabase
      .from('friendships')
      .select('friend_id')
      .eq('user_id', me)
      .eq('status', 'friend');
    const friendIds = (fr || []).map((r) => r.friend_id);
    const ids = [me, ...friendIds];
    const { data, error } = await supabase
      .from('personal_runs')
      .select('id, user_id, distance_km, duration_sec, visibility, created_at, profiles(name, avatar_url)')
      .in('user_id', ids)
      .neq('visibility', 'private')
      .order('created_at', { ascending: false })
      .limit(limit);
    // If `visibility !== private` filter is too strict (column maybe missing),
    // fall back to a softer query so the tab isn't empty for newer users.
    let rows = data;
    if (error || !rows || rows.length === 0) {
      const fallback = await supabase
        .from('personal_runs')
        .select('id, user_id, distance_km, duration_sec, created_at, profiles(name, avatar_url)')
        .in('user_id', ids)
        .order('created_at', { ascending: false })
        .limit(limit);
      rows = fallback.data || [];
    }
    return (rows || []).map((r) => ({
      id: r.id,
      userId: r.user_id,
      userName: r.profiles?.name || (r.user_id === me ? 'You' : 'A runner'),
      avatarUrl: r.profiles?.avatar_url || null,
      distanceKm: Number(r.distance_km) || 0,
      durationSec: Number(r.duration_sec) || 0,
      verified: false,
      type: 'run',
      createdAt: r.created_at,
    }));
  },

  // Send a friend invite by email or handle.
  // Tries to find an existing profile first — if found, creates a friendship request.
  // Otherwise stores the invite in `friend_invites` for the recipient to claim on signup.
  async sendFriendInvite({ email, handle }) {
    if (!supabase) return { sent: true, mode: 'mock' };
    const me = await uid();
    if (!me) throw new Error('Not signed in');
    const q = email ? { column: 'email', value: email.toLowerCase() } : { column: 'handle', value: handle };
    if (!q.value) throw new Error('Provide an email or username');
    const { data: target } = await supabase
      .from('profiles')
      .select('id')
      .eq(q.column, q.value)
      .maybeSingle();
    if (target?.id) {
      if (target.id === me) throw new Error("You can't friend yourself");
      const { error } = await supabase.from('friendships').upsert(
        { user_id: me, friend_id: target.id, status: 'pending' },
        { onConflict: 'user_id,friend_id' }
      );
      throwIf(error);
      return { sent: true, mode: 'request' };
    }
    // No profile yet — record a pending invite so we can match when they register.
    // We do a check-then-insert because friend_invites has *partial* unique
    // indexes (per email, per handle) which PostgREST's onConflict can't target.
    const emailKey = email ? email.toLowerCase() : null;
    const handleKey = handle || null;
    let existsQuery = supabase.from('friend_invites').select('id').eq('inviter_id', me);
    existsQuery = emailKey ? existsQuery.eq('email', emailKey) : existsQuery.eq('handle', handleKey);
    const { data: existing } = await existsQuery.maybeSingle();
    if (existing?.id) return { sent: true, mode: 'pending_signup' };
    const { error } = await supabase
      .from('friend_invites')
      .insert({ inviter_id: me, email: emailKey, handle: handleKey });
    throwIf(error);
    return { sent: true, mode: 'pending_signup' };
  },

  async getFriends() {
    if (!supabase) return mock.mockFriends;
    const id = await uid();
    if (!id) return [];
    const { data, error } = await supabase
      .from('friendships')
      .select('status, friend_id, profile:profiles!friendships_friend_id_fkey(id,name,handle,avatar_url)')
      .eq('user_id', id);
    throwIf(error);
    return (data || []).map(mapFriend);
  },

  // Pending requests sent TO me (incoming)
  async getFriendRequests() {
    if (!supabase) return [];
    const id = await uid();
    if (!id) return [];
    const { data, error } = await supabase
      .from('friendships')
      .select('status, user_id, profile:profiles!friendships_user_id_fkey(id,name,handle,avatar_url)')
      .eq('friend_id', id)
      .eq('status', 'pending');
    throwIf(error);
    return (data || []).map((f) => ({
      id: f.profile?.id || f.user_id,
      name: f.profile?.name || 'Unknown',
      handle: f.profile?.handle ? '@' + f.profile.handle : '—',
      avatar: f.profile?.avatar_url || null,
      status: f.status,
    }));
  },

  async friendDecline(requesterId) {
    if (!supabase) return;
    const me = await uid();
    if (!me) return;
    // Delete BOTH directions so a stale pending row doesn't linger on either side.
    const { error } = await supabase
      .from('friendships')
      .delete()
      .or(`and(user_id.eq.${requesterId},friend_id.eq.${me}),and(user_id.eq.${me},friend_id.eq.${requesterId})`)
      .eq('status', 'pending');
    throwIf(error);
  },

  async friendRequest(friendId) {
    if (!supabase) return;
    const { error } = await supabase.rpc('rpc_friend_request', { p_friend_id: friendId });
    throwIf(error);
  },

  async friendAccept(requesterId) {
    if (!supabase) return;
    const { error } = await supabase.rpc('rpc_friend_accept', { p_requester_id: requesterId });
    throwIf(error);
  },

  // ---------- Notifications (aggregated client-side) ----------
  // Returns the user's notification feed by aggregating three sources:
  //   1. Incoming pending friend requests
  //   2. Events the user has joined that start within the next 1 hour
  //   3. Events the user has joined where the race has just gone live
  // Each entry: { id, kind, title, body, createdAt, link, extra }
  async getNotifications() {
    if (!supabase) return [];
    const id = await uid();
    if (!id) return [];

    const out = [];

    // 1. Friend requests
    const reqs = await this.getFriendRequests();
    for (const r of reqs) {
      out.push({
        id: `friend-${r.id}`,
        kind: 'friend_request',
        title: 'New friend request',
        body: `${r.name} (${r.handle}) wants to connect`,
        createdAt: new Date().toISOString(),
        link: '/friends?tab=requests',
        extra: { requesterId: r.id },
      });
    }

    // 2 & 3. Events I've joined — only fetch the ones we care about (upcoming + live)
    const { data: parts } = await supabase
      .from('participations')
      .select('event_id, state, events(id,name,date,race_state,race_started_at)')
      .eq('user_id', id)
      .in('state', ['joined', 'ready', 'live']);

    const nowMs = Date.now();
    const HOUR_MS = 60 * 60 * 1000;
    for (const p of parts || []) {
      const ev = p.events;
      if (!ev) continue;

      // Live: race started — surface as a "race started" notification
      if (ev.race_state === 'live') {
        out.push({
          id: `event-live-${ev.id}`,
          kind: 'event_started',
          title: 'Race has started',
          body: `${ev.name} is live — head to the start line`,
          createdAt: ev.race_started_at || new Date().toISOString(),
          link: `/events/${ev.id}`,
        });
        continue;
      }

      // Upcoming: starts within the next 1h (and not already passed)
      const startMs = new Date(ev.date).getTime();
      const delta = startMs - nowMs;
      if (delta > 0 && delta <= HOUR_MS) {
        const mins = Math.max(1, Math.round(delta / 60000));
        out.push({
          id: `event-soon-${ev.id}`,
          kind: 'event_starting_soon',
          title: 'Race starting soon',
          body: `${ev.name} starts in ${mins} min`,
          createdAt: new Date().toISOString(),
          link: `/events/${ev.id}`,
        });
      }
    }

    // Newest first
    return out.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  },

  // ---------- Personal runs ----------
  async getMyRuns() {
    if (!supabase) return mock.mockPersonalRuns;
    const id = await uid();
    if (!id) return [];
    const { data, error } = await supabase
      .from('personal_runs')
      .select('*')
      .eq('user_id', id)
      .order('date', { ascending: false })
      .limit(20);
    throwIf(error);
    return (data || []).map(mapRun);
  },

  async savePersonalRun({ distanceKm, durationSec, visibility = 'private', gpsTrack = null, startedAt = null }) {
    if (!supabase) return { id: 'mock-run-' + Date.now() };
    const id = await uid();
    if (!id) throw new Error('Not signed in');
    const paceSec = distanceKm > 0 ? Math.round(durationSec / distanceKm) : null;
    const payload = {
      user_id: id,
      distance_km: distanceKm,
      duration_sec: durationSec,
      pace_sec_per_km: paceSec,
      visibility,
      gps_track: gpsTrack,
    };
    // Use the runner's wall-clock start time (not the DB insert time) so
    // the "Logged at" timestamp in history reflects when the run actually
    // began, not when it was saved. We stamp BOTH `date` and `created_at`
    // to the true start:
    //   • `date` — the primary column, but pre-round17 it's DATE type so
    //     Postgres will still truncate to a calendar day. Harmless.
    //   • `created_at` — timestamptz, always preserves the full timestamp.
    //     mapRun() prefers this when `date` looks truncated, so the fix
    //     works even without running round17_delta.sql.
    if (startedAt) {
      const iso = new Date(startedAt).toISOString();
      payload.date = iso;
      payload.created_at = iso;
    }
    const { data, error } = await supabase
      .from('personal_runs')
      .insert(payload)
      .select()
      .single();
    throwIf(error);
    return data;
  },

  async getRunById(runId) {
    if (!supabase || !runId) return null;
    const { data, error } = await supabase
      .from('personal_runs')
      .select('*')
      .eq('id', runId)
      .maybeSingle();
    throwIf(error);
    return data ? mapRun(data) : null;
  },

  // ---------- Race lifecycle (organizer) ----------
  async startRace(eventId) {
    if (!supabase) return 0;
    const { data, error } = await supabase.rpc('rpc_start_race', { p_event_id: eventId });
    throwIf(error);
    return data;
  },

  async endRace(eventId) {
    if (!supabase) return 0;
    const { data, error } = await supabase.rpc('rpc_end_race', { p_event_id: eventId });
    throwIf(error);
    return data;
  },

  // ---------- Race lifecycle (RPCs) ----------
  async joinEvent(eventId, bib = null) {
    if (!supabase) return { id: 'mock-part-' + Date.now() };
    const { data, error } = await supabase.rpc('rpc_join_event', { p_event_id: eventId, p_bib: bib });
    throwIf(error);

    // Fire-and-forget payment-confirmation email if this is a paid event.
    try {
      const { data: ev } = await supabase
        .from('events')
        .select('name, date, fee, currency, is_paid')
        .eq('id', eventId)
        .maybeSingle();
      if (ev?.is_paid) {
        const { data: { user } = {} } = await supabase.auth.getUser();
        if (user?.email) {
          supabase.functions.invoke('send-email', {
            body: {
              kind: 'payment-confirmed',
              to: user.email,
              eventName: ev.name,
              eventDate: ev.date,
              fee: ev.fee,
              currency: ev.currency,
              bib,
            },
          }).catch((e) => console.warn('payment email failed:', e?.message));
        }
      }
    } catch (e) {
      console.warn('payment email skipped:', e?.message);
    }

    return { id: data };
  },

  async checkIn(eventId) {
    if (!supabase) return;
    const { error } = await supabase.rpc('rpc_check_in', { p_event_id: eventId });
    throwIf(error);
  },

  async markReady(eventId) {
    if (!supabase) return;
    const { error } = await supabase.rpc('rpc_mark_ready', { p_event_id: eventId });
    throwIf(error);
  },

  async finishRace(eventId, finishTimeSec, gpsTrack = null, coveragePct = 95) {
    if (!supabase) return;
    const { error } = await supabase.rpc('rpc_finish_race', {
      p_event_id: eventId,
      p_finish_time_sec: finishTimeSec,
      p_gps_track: gpsTrack,
      p_coverage_pct: coveragePct,
    });
    throwIf(error);
  },

  async getMyParticipation(eventId) {
    if (!supabase) return mock.mockParticipation;
    const id = await uid();
    if (!id) return null;
    const { data, error } = await supabase
      .from('participations')
      .select('*')
      .eq('event_id', eventId)
      .eq('user_id', id)
      .maybeSingle();
    throwIf(error);
    return data ? mapParticipation(data) : null;
  },

  // ---------- Scanner ----------
  async scanMedal(qrToken, eventId, scannerName = 'Scanner') {
    if (!supabase) {
      // In mock mode, valid 70% of the time
      return Math.random() > 0.3 ? 'valid' : 'invalid_token';
    }
    const { data, error } = await supabase.rpc('rpc_scan_medal', {
      p_qr_token: qrToken,
      p_event_id: eventId,
      p_scanner_name: scannerName,
    });
    throwIf(error);
    return data;
  },

  // Pull runner card details for a scanned QR (name, bib, time, coverage, rank)
  async getRunnerByQR(qrToken, eventId) {
    if (!supabase) {
      return {
        name: 'Demo Runner',
        bib: '0421',
        avatarUrl: null,
        finishTime: '01:42:18',
        finishTimeSec: 6138,
        paceMinPerKm: '4:51',
        coverage: 98,
        rank: 14,
        verified: true,
      };
    }
    // Fetch participation + profile + event distance + rank in event
    const { data, error } = await supabase
      .from('participations')
      .select('id, bib_number, finish_time_sec, coverage_pct, state, event_id, profiles(name, avatar_url), events(distance_km)')
      .eq('qr_token', qrToken)
      .eq('event_id', eventId)
      .maybeSingle();
    throwIf(error);
    if (!data) return null;
    // Compute rank from leaderboard view
    let rank = null;
    const { data: lb } = await supabase
      .from('v_leaderboard')
      .select('participation_id, rank')
      .eq('event_id', eventId)
      .eq('participation_id', data.id)
      .maybeSingle();
    if (lb) rank = lb.rank;
    const dist = Number(data.events?.distance_km || 0);
    const sec = data.finish_time_sec || 0;
    const finishTime = sec
      ? `${String(Math.floor(sec / 3600)).padStart(2, '0')}:${String(Math.floor((sec % 3600) / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`
      : '—';
    return {
      name: data.profiles?.name || 'Unknown',
      bib: data.bib_number || '—',
      avatarUrl: data.profiles?.avatar_url || null,
      finishTime,
      finishTimeSec: sec,
      paceMinPerKm: paceFromTime(sec, dist),
      coverage: data.coverage_pct ?? null,
      rank,
      verified: data.state === 'verified',
    };
  },

  // ---------- Create event (mobile-native form) ----------
  async createEvent(form) {
    if (!supabase) return { id: 'mock-event-' + Date.now() };
    const id = await uid();
    if (!id) throw new Error('Not signed in');
    const payload = {
      name: form.name,
      organizer_id: id,
      organizer_name: form.organizerName || null,
      type: form.type || 'community',
      date: form.date,
      distance_km: parseFloat(form.distance) || 5,
      location: form.location,
      capacity: form.capacity ? parseInt(form.capacity, 10) || null : null,
      description: form.description,
      bib_mode: form.bibMode || 'none',
      medal_enabled: !!form.medalEnabled,
      fee: parseFloat(form.fee) || 0,
      currency: 'NGN',
      is_paid: !!form.isPaid,
      status: 'open',
      image: form.image || null,
      start_lat: form.startLat ?? null,
      start_lng: form.startLng ?? null,
      finish_lat: form.finishLat ?? null,
      finish_lng: form.finishLng ?? null,
      event_mode: form.eventMode === 'virtual' ? 'virtual' : 'physical',
      checkpoints: Array.isArray(form.checkpoints) ? form.checkpoints : [],
      activation_code: form.activationCode || null,
      join_deadline: form.joinDeadline || null,
      bank_name: form.bankName || null,
      account_number: form.accountNumber || null,
      account_name: form.accountName || null,
    };
    const { data, error } = await supabase.from('events').insert(payload).select().single();
    throwIf(error);

    // Auto-join the organizer as a participant on Community events (both
    // Physical and Virtual). This mirrors the real-world intent: whoever
    // creates a "run with friends" event is themselves running it.
    // For Official events organizers usually stay in a race-director role,
    // so we skip auto-join there.
    if (data?.id && (form.type === 'community' || !form.type)) {
      try {
        await supabase.rpc('rpc_join_event', { p_event_id: data.id, p_bib: null });
      } catch { /* best-effort — organizer can still join manually */ }
    }

    // Fire-and-forget activation-code email to the organizer when relevant.
    // We don't await — a Resend failure shouldn't block event creation.
    if (form.medalEnabled && form.activationCode) {
      try {
        const { data: { user } = {} } = await supabase.auth.getUser();
        const organizerEmail = user?.email;
        if (organizerEmail) {
          supabase.functions.invoke('send-email', {
            body: {
              kind: 'organizer-activation',
              to: organizerEmail,
              eventName: form.name,
              code: form.activationCode,
              eventId: data.id,
            },
          }).catch((e) => console.warn('activation email failed:', e?.message));
        }
      } catch (e) {
        console.warn('activation email skipped:', e?.message);
      }
    }

    return mapEvent(data);
  },

  async uploadEventImage(file) {
    if (!supabase) throw new Error('Supabase not configured');
    if (!file) throw new Error('No file selected');
    const id = await uid();
    if (!id) throw new Error('Not signed in');
    const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase();
    const path = `${id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from('event-images')
      .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });
    if (upErr) throw upErr;
    const { data } = supabase.storage.from('event-images').getPublicUrl(path);
    return data?.publicUrl;
  },

  // Delete an event (organizer or admin only — RLS enforces this).
  // Cascades to participations / activity_feed / medal_scans via ON DELETE CASCADE.
  // Returns the number of rows actually deleted so callers can distinguish
  // real success from an RLS-blocked silent no-op.
  async deleteEvent(eventId) {
    const { data, error } = await supabase
      .from('events')
      .delete()
      .eq('id', eventId)
      .select('id');
    throwIf(error);
    const deletedCount = Array.isArray(data) ? data.length : 0;
    if (deletedCount === 0) {
      throw new Error('You do not have permission to delete this event.');
    }
    return { ok: true, deleted: deletedCount };
  },

  // Upsert a native push token (FCM on Android, APNs on iOS) for the
  // signed-in user so a backend edge function can target them. The DB
  // table (`device_tokens`) is shared with the web-push flow — we just
  // mark the row with provider='fcm' or 'apns' so Edge Functions know
  // which pipeline to use.
  async savePushToken({ token, provider, userAgent = null }) {
    if (!supabase || !token || !provider) return null;
    const id = await uid();
    if (!id) return null;
    const row = {
      user_id: id,
      provider, // 'fcm' | 'apns' | 'webpush'
      endpoint: token,
      p256dh: null,
      auth: null,
      user_agent: userAgent,
      updated_at: new Date().toISOString(),
    };
    const { error } = await supabase
      .from('device_tokens')
      .upsert(row, { onConflict: 'endpoint' });
    throwIf(error);
    return { ok: true };
  },


  // Uploads a new avatar image for the signed-in user and updates
  // profiles.avatar_url. Returns the public URL. Storage bucket: `avatars`.
  async uploadAvatar(file) {
    if (!supabase) throw new Error('Supabase not configured');
    if (!file) throw new Error('No file selected');
    const id = await uid();
    if (!id) throw new Error('Not signed in');
    const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase();
    const path = `${id}/avatar-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from('avatars')
      .upload(path, file, { cacheControl: '3600', upsert: true, contentType: file.type });
    if (upErr) throw upErr;
    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    const publicUrl = data?.publicUrl;
    if (publicUrl) {
      await supabase.from('profiles').update({ avatar_url: publicUrl }).eq('id', id);
    }
    return publicUrl;
  },

  // Search profiles by name or handle (case-insensitive). Excludes the caller
  // and anyone already in a friendship row with them (pending or accepted).
  // Returns at most `limit` rows: [{ id, name, handle, avatarUrl }]
  async searchProfiles(query, limit = 10) {
    if (!supabase) return [];
    const q = (query || '').trim();
    if (q.length < 2) return [];
    const me = await uid();
    // Profiles matching name OR handle ILIKE %q%
    const pattern = `%${q.replace(/[%_]/g, (m) => '\\' + m)}%`;
    const { data, error } = await supabase
      .from('profiles')
      .select('id, name, handle, avatar_url')
      .or(`name.ilike.${pattern},handle.ilike.${pattern}`)
      .limit(limit + 10);
    throwIf(error);
    let rows = (data || []).filter((r) => r.id !== me);

    // Exclude anyone the user already has a friendship row with (either direction).
    if (me && rows.length) {
      const { data: existing } = await supabase
        .from('friendships')
        .select('user_id, friend_id')
        .or(`user_id.eq.${me},friend_id.eq.${me}`);
      const blocked = new Set();
      for (const r of existing || []) {
        blocked.add(r.user_id === me ? r.friend_id : r.user_id);
      }
      rows = rows.filter((r) => !blocked.has(r.id));
    }
    return rows.slice(0, limit).map((r) => ({
      id: r.id,
      name: r.name || 'Runner',
      handle: r.handle ? '@' + r.handle : '',
      avatarUrl: r.avatar_url || null,
    }));
  },

  // ---------- Leaderboards (extended) ----------
  // Global: best verified finish time across all events (top 50)
  async getGlobalLeaderboard() {
    if (!supabase) return mock.mockLeaderboard;
    const me = await uid();
    const { data, error } = await supabase
      .from('v_leaderboard')
      .select('*')
      .eq('verified', true)
      .order('finish_time_sec', { ascending: true })
      .limit(50);
    throwIf(error);
    // Re-rank globally so #1 has the fastest time
    return (data || []).map((row, i) => mapLeaderboardRow({ ...row, rank: i + 1, distance_km: 0 }, me));
  },

  // Friends: leaderboard rows where the runner is one of my accepted friends
  async getFriendsLeaderboard(eventId = null) {
    if (!supabase) return mock.mockLeaderboard;
    const me = await uid();
    if (!me) return [];
    const { data: fr } = await supabase
      .from('friendships')
      .select('friend_id')
      .eq('user_id', me)
      .eq('status', 'friend');
    const ids = (fr || []).map((r) => r.friend_id).concat(me);
    if (!ids.length) return [];
    let q = supabase.from('v_leaderboard').select('*').in('user_id', ids).order('finish_time_sec', { ascending: true }).limit(50);
    if (eventId) q = q.eq('event_id', eventId);
    const { data, error } = await q;
    throwIf(error);
    return (data || []).map((row, i) => mapLeaderboardRow({ ...row, rank: i + 1, distance_km: 0 }, me));
  },
};
